// src/pages/dashboards/user/UserSplits.jsx
// Shared deductions: incoming (pending, awaiting my decision) and outgoing (sent).

import React, { useEffect, useState } from "react";
import { splitsSent, splitsPending, acceptSplit, declineSplit, cancelSplit } from "../../../api/user";
import { Card, Button, Badge, Loading, EmptyState } from "../../../components/ui";
import { useToast } from "../../../context/ToastContext";
import { money, date, titleCase } from "../../../lib/format";

const TONE = { pending: "amber", claimed: "green", cancelled: "gray" };

export default function UserSplits() {
  const toast = useToast();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState(null);
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    splitsPending().then(setPending).catch((e) => toast.error(e.message));
    splitsSent().then(setSent).catch((e) => toast.error(e.message));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const list = tab === "pending" ? pending : sent;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Shared deductions</h1>

      <div className="inline-flex rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-1 mb-5">
        {["pending", "sent"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    tab === t ? "surface text-tb-ink dark:text-tb-d-text shadow-sm" : "text-tb-muted"}`}>
            {t === "pending" ? "Shared with me" : "Shared by me"}
            {t === "pending" && pending?.length ? ` (${pending.length})` : ""}
          </button>
        ))}
      </div>

      {!list ? <Loading label="Loading shared deductions" /> : list.length === 0 ? (
        <Card className="p-0">
          <EmptyState
            title={tab === "pending" ? "Nothing shared with you" : "Nothing shared yet"}
            hint={tab === "pending"
              ? "When someone shares part of a receipt with you, it appears here to accept or decline."
              : "Portions you share from the app appear here."}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((s) => (
            <Card key={s.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{s.merchant || "Receipt"}</p>
                  <p className="text-xs text-tb-muted mt-0.5">
                    {date(s.receipt_date)}
                    {tab === "sent" && s.recipient_phone ? ` · to ${s.recipient_phone}` : ""}
                    {tab === "pending" && s.sender_name ? ` · from ${s.sender_name}` : ""}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold tabular-nums">{money(s.split_total)}</p>
                  <Badge tone={TONE[s.status] || "gray"}>{titleCase(s.status)}</Badge>
                </div>
              </div>

              {tab === "pending" && s.status === "pending" && (
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="ghost" className="text-red-600" onClick={() => act(() => declineSplit(s.id), "Declined")} disabled={busy}>Decline</Button>
                  <Button onClick={() => act(() => acceptSplit(s.id), "Accepted - added to your deductions")} disabled={busy}>Accept</Button>
                </div>
              )}
              {tab === "sent" && s.status === "pending" && (
                <div className="flex justify-end mt-3">
                  <Button variant="ghost" className="text-red-600" onClick={() => act(() => cancelSplit(s.id), "Cancelled")} disabled={busy}>Cancel</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}